/**
 * <cw-address>: "Set delivery location" (design-handoff/components/address.md, Shopping v13,
 * Desktop v2). Opens on 'cw:address:open' (the header's address pill, Home's delivery block,
 * the basket's "Set location").
 *
 * Who does what (as the live theme's "Deliver to" control, whose rules are kept):
 * - Google, straight from the page: place suggestions (Places API, kept to a circle round the
 *   mill so a far namesake never comes first), the place's details, the map, and the name of
 *   the spot under the pin. The key comes from Flow's config answer, never from the theme.
 * - Flow: the distance and the promise for the spot (cw-delivery.js quote()), asked once the
 *   pin settles, and again whenever it moves.
 * - This browser keeps the address book (localStorage 'chakki.deliverTo.v1', the same record
 *   the live theme keeps, so an address set there is here too). Saved addresses are not yet
 *   Shopify account addresses: the shop's new customer accounts cannot be written from the
 *   theme (design-handoff/open-questions.md).
 *
 * Rules from the design: picking a saved card chooses it and closes the sheet; an address
 * beyond Flow's delivery edge can still be chosen, as pick up only (v13 1j); the chosen
 * address cannot be deleted; one address can be pinned to the top; saving chooses it.
 * A card never shows a fee.
 */
import { CwSheet } from '@cw/sheet';
import { book, saveBook, settings, quote, isComplete } from '@cw/delivery';
import { syncBook, pushAddress, dropAddress } from '@cw/book-sync';

const MIN_LETTERS = 3;
const TYPING_PAUSE = 320;
const KINDS = [
  ['home', 'Home'],
  ['work', 'Work'],
  ['family', 'Family'],
  ['other', 'Other'],
];

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : '');

/** "800 m", "3.9 km". */
function kmText(km) {
  const k = Number(km);
  if (km == null || !Number.isFinite(k)) return '';
  return k < 1 ? `${Math.round(k * 1000)} m` : `${Math.round(k * 10) / 10} km`;
}

/** Ten digits, starting 6-9; a pasted +91 or leading 0 is dropped. */
function tenDigits(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
}
const phoneOk = (raw) => /^[6-9]\d{9}$/.test(tenDigits(raw));
const phoneText = (raw) => {
  const d = tenDigits(raw);
  return d.length === 10 ? `${d.slice(0, 5)} ${d.slice(5)}` : String(raw || '');
};

/**
 * Google writes the whole postal truth, repeated ("Neeladri Investment Layout, Doddathoguru,
 * Neeladri Investment Layout, …, Karnataka 560100, India"). Keep the first few distinct
 * pieces and the pincode, what a person would say out loud (the live theme's rule).
 */
function shortAddress(text) {
  const seen = new Set();
  const kept = [];
  for (const part of String(text || '').split(',').map((p) => p.trim()).filter(Boolean)) {
    const k = part.toLowerCase();
    if (k === 'india' || seen.has(k)) continue;
    seen.add(k);
    kept.push(part);
  }
  const pin = (String(text || '').match(/\b\d{6}\b/) || [])[0];
  const head = kept.slice(0, 3);
  if (pin && !head.join(' ').includes(pin)) head.push(pin);
  return head.join(', ');
}

/** The address line under a name, without the name repeated ("Electronic City Phase I, …"). */
function addressUnder(name, text) {
  const own = String(name || '').trim().toLowerCase();
  const rest = String(text || '')
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p && p.toLowerCase() !== own)
    .join(', ');
  return shortAddress(rest);
}

/** The pieces of a place a rider needs, from Google's address components. */
function partsOf(components = [], long = 'longText') {
  const out = { pincode: '', area: '', city: '', street: '' };
  let wide = '';
  for (const c of components) {
    const types = c.types || [];
    const text = c[long] || c.long_name || '';
    if (types.includes('postal_code')) out.pincode = text;
    if (!out.area && (types.includes('sublocality_level_2') || types.includes('sublocality_level_1') || types.includes('sublocality') || types.includes('neighborhood'))) out.area = text;
    if (!wide && types.includes('sublocality_level_1')) wide = text;
    if (!out.city && types.includes('locality')) out.city = text;
    if (!out.street && types.includes('route')) out.street = text;
  }
  // "Phase 1", "Sector 2", "5th Block" name a part of a bigger area, and alone they name
  // nothing a rider can find: "Electronic City Phase 1", "HSR Layout Sector 2".
  if (wide && out.area && out.area !== wide && /\d|^(phase|stage|sector|block|layout)\b/i.test(out.area) && !out.area.toLowerCase().includes(wide.toLowerCase())) {
    out.area = `${wide} ${out.area}`;
  }
  if (!out.area) out.area = out.city;
  return out;
}

/**
 * The building a spot is in, from Google's answer for it, or '' (owner, 2 Oct: the society name
 * is what a rider needs, and "Gunjur Village" is an area, not a building). A result counts when
 * Google says it is a building or a place (premise, establishment…); its own name, else the
 * first part of its address. Never an area, a road or a plus code.
 */
const BUILDING_TYPES = ['premise', 'subpremise', 'establishment', 'point_of_interest'];
function buildingFrom(results = []) {
  for (const r of results) {
    if (!(r.types || []).some((t) => BUILDING_TYPES.includes(t))) continue;
    const comp = (r.address_components || []).find((c) => (c.types || []).some((t) => BUILDING_TYPES.includes(t)));
    const name = String(comp?.long_name || String(r.formatted_address || '').split(',')[0]).trim();
    if (name && /[a-z]{3}/i.test(name) && !/^[A-Z0-9]{4}\+[A-Z0-9]{2,}/.test(name)) return name;
  }
  return '';
}

/** Google's kinds of place that are areas or roads: a picked place of these is not a building. */
const AREA_TYPES = ['locality', 'sublocality', 'sublocality_level_1', 'sublocality_level_2', 'sublocality_level_3', 'neighborhood', 'political', 'route', 'postal_code', 'administrative_area_level_1', 'administrative_area_level_2', 'administrative_area_level_3', 'country', 'plus_code', 'intersection'];

/** The old theme's stand-in name for a pin with no name (see cw-delivery.js isComplete). */
const NO_NAME = 'That address';

const kindOf = (a) => a.kind || { home: 'home', work: 'work', family: 'family' }[String(a.tag || '').toLowerCase()] || 'other';

class CwAddress extends CwSheet {
  #timer = 0;
  #mtimer = 0;
  #token = null;
  #seq = 0;
  #mapsPromise = null;
  #settings = null;
  #measuring = null;

  connectedCallback() {
    super.connectedCallback();
    this.root = this.querySelector('.cw-addr');
    this.icons = {};
    for (const el of this.querySelector('template[data-cw="icons"]').content.querySelectorAll('[data-icon]')) {
      this.icons[el.dataset.icon] = el.innerHTML;
    }
    this.fm = null;
    this.draft = null;
    document.addEventListener('cw:address:open', this.#onRequest);
    document.addEventListener('cw:delivery:change', this.#onDelivery);
    this.addEventListener('click', this.#onAct);
    this.querySelector('[data-cw="q"]').addEventListener('input', this.#onQuery);
    this.querySelector('[data-cw="mq"]').addEventListener('input', this.#onMapQuery);
    const form = this.querySelector('[data-cw="form"]');
    form.addEventListener('input', this.#onField);
    form.addEventListener('focusout', this.#onBlur);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#save();
    });
    settings()
      .then((s) => {
        this.#settings = s;
        if (this.isOpen && this.root.dataset.view === 'list') this.#paintList();
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cw:address:open', this.#onRequest);
    document.removeEventListener('cw:delivery:change', this.#onDelivery);
  }

  /** The list scrolls in its own body; the map and the form never close by a swipe. */
  get body() {
    return this.querySelector('[data-cw="list-body"]');
  }

  swipeable() {
    return this.root.dataset.view === 'list' && this.querySelector('[data-cw="menu"]').hidden;
  }

  /** Esc takes away the top layer only: the menu, the map search, the form, then the map. */
  onEscape() {
    if (!this.querySelector('[data-cw="menu"]').hidden) {
      this.#closeMenu();
      return true;
    }
    const msearch = this.querySelector('[data-cw="msearch"]');
    if (!msearch.hidden) {
      msearch.hidden = true;
      return true;
    }
    const view = this.root.dataset.view;
    if (view === 'form') {
      this.querySelector('.cw-addr__formhead [data-act="form-close"]').click();
      return true;
    }
    if (view === 'map') {
      this.querySelector('.cw-addr__mapview [data-act="back"]').click();
      return true;
    }
    return false;
  }

  onClosed() {
    this.#view('list');
    this.#closeMenu();
    this.#clearQuery();
  }

  #onRequest = (event) => {
    event.preventDefault();
    // "Set location" (the header, Home, the basket) is the spot only: CONFIRM LOCATION saves it
    // and the delivery time shows. The flat and building are asked at CHECKOUT, or when the
    // shopper adds an address on purpose ("Add new address", editing). Owner, 2 Oct.
    this.mode = event.detail?.mode === 'address' ? 'address' : 'location';
    this.#view('list');
    this.#paintList();
    this.open(event.detail?.source);
    // CHECKOUT with an incomplete address (cw-delivery.js) opens straight on its form.
    if (event.detail?.editId) this.#editFor(event.detail.editId, !!event.detail.ask);
  };

  /** The form for a saved address; `ask` adds the line saying what is missing and why. */
  async #editFor(id, ask = false) {
    const a = book().saved.find((x) => x.id === id);
    this.querySelector('[data-cw="menu"]').hidden = true;
    if (!a) return;
    this.fm = { ...this.#formFrom(a), ask };
    if (a.lat == null || a.lng == null) {
      // From the account with no pin anywhere (its lookup failed): the map first, at the
      // mill, and the form once the shopper has put the pin on their door.
      const s = this.#settings || (this.#settings = await settings().catch(() => null));
      const start = s?.origin || { lat: 12.8192, lng: 77.6638 };
      return this.#toMap({ lat: start.lat, lng: start.lng, address: '', name: a.apartment || '', named: false }, 'Edit address');
    }
    this.draft = { lat: a.lat, lng: a.lng, address: a.address, area: a.area, street: a.street, city: a.city, pincode: a.pincode, km: a.km, name: a.apartment || a.building || a.area || '', building: a.apartment || a.building || '', named: true };
    this.#toForm();
    // An old address can lack its pincode and area: the spot under its pin fills them in.
    if (!a.pincode) this.#reverse(true).then(() => this.fm && this.root.dataset.view === 'form' && this.#paintForm());
  }

  #onDelivery = () => {
    if (this.isOpen && this.root.dataset.view === 'list') this.#paintList();
  };

  /** list | map | form (the form sits over the map). */
  #view(name) {
    this.root.dataset.view = name;
    this.root.classList.toggle('is-full', name !== 'list');
    if (name !== 'map') this.querySelector('[data-cw="msearch"]').hidden = true;
    if (!this.isOpen) return;
    requestAnimationFrame(() => {
      const target =
        name === 'list'
          ? null
          : name === 'map'
            ? this.querySelector('.cw-addr__mapview .cw-addr__back')
            : this.querySelector('.cw-addr__formhead .cw-addr__x');
      target?.focus({ preventScroll: true });
    });
  }

  /* ---------------------------------------------------------------- list */

  #svc(a) {
    if (!isComplete(a)) return { tone: 'ask', text: 'Add flat and building' };
    const s = this.#settings;
    const max = s?.max_km ?? 35;
    if (a.km != null && Number(a.km) > max) return { tone: 'pickup', text: 'Pick up from our shop only' };
    const when = s?.standard?.when;
    if (!when) return { tone: 'today', text: 'We deliver here' };
    return { tone: /^tomorrow/i.test(when) ? 'tomorrow' : 'today', text: `Delivers ${lowerFirst(when)}` };
  }

  #paintList() {
    const b = book();
    const list = b.saved.slice().sort((x, y) => (y.id === b.pinnedId) - (x.id === b.pinnedId));
    const host = this.querySelector('[data-cw="saved"]');
    this.querySelector('[data-cw="saved-head"]').hidden = list.length === 0;
    host.innerHTML = list
      .map((a) => {
        const on = a.id === b.chosenId;
        const pinned = a.id === b.pinnedId;
        const svc = this.#svc(a);
        const kind = kindOf(a);
        const label = a.tag || { home: 'Home', work: 'Work', family: 'Family' }[kind] || (!a.flat && !a.apartment && !a.lines ? 'Delivery location' : 'Other');
        // An address from the account, as checkout shows it (cw-book-sync.js); ours, from its fields.
        const apt = a.lines ? a.lines.address1 : a.apartment || (a.label !== NO_NAME ? a.label : '') || '';
        const lines = a.lines
          ? [a.lines.address2, a.pincode].filter(Boolean).join(', ')
          : [a.flat, a.tower, a.floor ? `Floor ${a.floor}` : '', addressUnder(apt, a.address) || a.area]
              .filter(Boolean)
              .filter((x, i, all) => all.indexOf(x) === i && x !== apt)
              .join(', ');
        const who = [a.name ? (a.me === false ? `For ${a.name}` : a.name) : '', a.phone ? phoneText(a.phone) : ''].filter(Boolean).join(' · ');
        return `
          <div class="cw-addr__item${on ? ' is-on' : ''}">
            <button type="button" class="cw-addr__pick" data-act="pick" data-id="${esc(a.id)}" aria-pressed="${on}">
              <span class="cw-addr__tile">${this.icons[kind] || this.icons.other}${on ? `<span class="cw-addr__tick">${this.icons.check}</span>` : ''}</span>
              <span class="cw-addr__itext">
                <span class="cw-addr__lrow"><span class="cw-addr__label">${esc(label)}</span>${a.km != null ? `<span class="cw-addr__km">· ${esc(kmText(a.km))}</span>` : ''}</span>
                ${apt ? `<span class="cw-addr__apt">${esc(apt)}</span>` : ''}
                ${lines ? `<span class="cw-addr__lines">${esc(lines)}</span>` : ''}
                ${who ? `<span class="cw-addr__who">${esc(who)}</span>` : ''}
                <span class="cw-addr__svc" data-tone="${svc.tone}">${this.icons.clock}<span>${esc(svc.text)}</span></span>
              </span>
            </button>
            <div class="cw-addr__ibtns">
              <button type="button" class="cw-addr__ibtn cw-addr__ibtn--pin${pinned ? ' is-pinned' : ''}" data-act="pin" data-id="${esc(a.id)}" aria-label="${pinned ? 'Unpin' : 'Pin to top'}" aria-pressed="${pinned}">${pinned ? this.icons.pinned : this.icons.pin}</button>
              <button type="button" class="cw-addr__ibtn" data-act="more" data-id="${esc(a.id)}" aria-label="More for ${esc(label)}">${this.icons.more}</button>
            </div>
          </div>`;
      })
      .join('');
  }

  #note(text) {
    const el = this.querySelector('[data-cw="note"]');
    el.textContent = text || '';
    el.hidden = !text;
  }

  #pick(id) {
    const b = book();
    const a = b.saved.find((x) => x.id === id);
    if (!a) return;
    b.chosenId = id;
    saveBook(b);
    // Chosen, but a rider could not find it yet: complete it now, not at checkout.
    if (!isComplete(a)) return this.#editFor(id, true);
    this.close();
  }

  #pin(id) {
    const b = book();
    b.pinnedId = b.pinnedId === id ? null : id;
    saveBook(b);
    this.#paintList();
  }

  /* -------------------------------------------------------------- search */

  #clearQuery() {
    for (const name of ['q', 'mq']) {
      const input = this.querySelector(`[data-cw="${name}"]`);
      if (input) input.value = '';
    }
    this.querySelector('[data-act="clear"]').hidden = true;
    this.querySelector('[data-act="mclear"]').hidden = true;
    this.querySelector('[data-cw="results"]').hidden = true;
    this.querySelector('[data-cw="results"]').innerHTML = '';
    this.querySelector('[data-cw="mresults"]').innerHTML = '';
    this.querySelector('[data-cw="home"]').hidden = false;
  }

  #onQuery = (event) => {
    const text = event.target.value.trim();
    this.querySelector('[data-act="clear"]').hidden = !event.target.value;
    clearTimeout(this.#timer);
    const results = this.querySelector('[data-cw="results"]');
    if (!text) {
      results.hidden = true;
      this.querySelector('[data-cw="home"]').hidden = false;
      return;
    }
    this.querySelector('[data-cw="home"]').hidden = true;
    results.hidden = false;
    if (text.length < MIN_LETTERS) return;
    this.#timer = setTimeout(() => this.#search(text, results), TYPING_PAUSE);
  };

  #onMapQuery = (event) => {
    const text = event.target.value.trim();
    this.querySelector('[data-act="mclear"]').hidden = !event.target.value;
    clearTimeout(this.#mtimer);
    const results = this.querySelector('[data-cw="mresults"]');
    if (text.length < MIN_LETTERS) {
      results.innerHTML = '';
      return;
    }
    this.#mtimer = setTimeout(() => this.#search(text, results), TYPING_PAUSE);
  };

  async #search(text, host) {
    let list = null;
    try {
      list = await this.#suggest(text);
    } catch {
      list = null;
    }
    const input = host.dataset.cw === 'mresults' ? this.querySelector('[data-cw="mq"]') : this.querySelector('[data-cw="q"]');
    if (input.value.trim() !== text) return; // they kept typing
    if (!list) {
      host.innerHTML = '<p class="cw-addr__none-sub">Search is not working just now. Use your current location, or try again in a moment.</p>';
      return;
    }
    if (!list.length) {
      host.innerHTML = `<div class="cw-addr__none"><p class="cw-addr__none-head">No places match “${esc(text)}”</p><p class="cw-addr__none-sub">Try an area, street or society name, or use your current location.</p></div>`;
      return;
    }
    const max = this.#settings?.max_km ?? 35;
    host.innerHTML = list
      .map((r) => {
        const out = r.km != null && r.km > max;
        return `
          <button type="button" class="cw-addr__res${out ? ' is-out' : ''}" data-act="place" data-id="${esc(r.id)}" data-main="${esc(r.main)}">
            <span class="cw-addr__res-pin"><span class="cw-addr__res-dot">${this.icons.mappin}</span>${r.km != null ? `<span class="cw-addr__res-km">${esc(kmText(r.km))}</span>` : ''}</span>
            <span class="cw-addr__res-text">
              <span class="cw-addr__res-name">${esc(r.main)}</span>
              ${r.sub ? `<span class="cw-addr__res-addr">${esc(r.sub)}</span>` : ''}
              ${out ? '<span class="cw-addr__res-out">We don’t deliver here yet</span>' : ''}
            </span>
          </button>`;
      })
      .join('');
  }

  /** Places (New) autocomplete, kept to a circle round the mill (the live theme's rule). */
  async #suggest(text) {
    const s = this.#settings || (this.#settings = await settings());
    if (!s.maps_key) return null;
    this.#token = this.#token || Math.random().toString(36).slice(2) + Date.now().toString(36);
    const center = { latitude: s.origin.lat, longitude: s.origin.lng };
    const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': s.maps_key },
      body: JSON.stringify({
        input: text,
        sessionToken: this.#token,
        includedRegionCodes: [s.country || 'in'],
        locationRestriction: { circle: { center, radius: s.bias_radius_m || 50000 } },
        origin: center,
      }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    return (data.suggestions || [])
      .map((x) => {
        const p = x.placePrediction || {};
        const f = p.structuredFormat || {};
        // Google's main text for an area carries the whole chain ("Neeladri Nagar, Electronic
        // City Phase I, Electronic City"): the first piece is the name, the rest the address.
        const main = f.mainText?.text || p.text?.text || '';
        const cut = main.indexOf(',');
        const name = cut > 0 ? main.slice(0, cut).trim() : main;
        const sub = [cut > 0 ? main.slice(cut + 1).trim() : '', f.secondaryText?.text || ''].filter(Boolean).join(', ');
        return { id: p.placeId, main: name, sub: shortAddress(sub), km: p.distanceMeters != null ? p.distanceMeters / 1000 : null };
      })
      .filter((x) => x.id);
  }

  /** One place's spot and postal parts; ends the search session (one billed lookup). */
  async #place(id, name) {
    const s = this.#settings || (this.#settings = await settings());
    const session = this.#token ? `&sessionToken=${encodeURIComponent(this.#token)}` : '';
    const response = await fetch(
      `https://places.googleapis.com/v1/places/${encodeURIComponent(id)}?fields=location,formattedAddress,addressComponents,types${session}`,
      { headers: { 'X-Goog-Api-Key': s.maps_key } }
    );
    this.#token = null;
    if (!response.ok) throw new Error('place');
    const p = await response.json();
    const parts = partsOf(p.addressComponents, 'longText');
    return {
      lat: p.location.latitude,
      lng: p.location.longitude,
      address: p.formattedAddress || '',
      name: name || parts.area,
      // A society or a building the shopper searched for is the building; an area is not.
      building: name && !(p.types || []).some((t) => AREA_TYPES.includes(t)) ? name : '',
      named: true,
      ...parts,
    };
  }

  /* ----------------------------------------------------------------- map */

  /** Google Maps, loaded only when somebody reaches the map: most visitors never do. */
  #maps() {
    if (this.#mapsPromise) return this.#mapsPromise;
    this.#mapsPromise = (async () => {
      const s = this.#settings || (this.#settings = await settings());
      if (!s.maps_key) throw new Error('no key');
      if (!window.google?.maps?.importLibrary) {
        await new Promise((resolve, reject) => {
          window.__cwMapsReady = resolve;
          const tag = document.createElement('script');
          tag.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(s.maps_key)}&v=weekly&loading=async&callback=__cwMapsReady`;
          tag.async = true;
          tag.onerror = () => reject(new Error('maps'));
          document.head.appendChild(tag);
        });
      }
      const [{ Map, Circle }, { Geocoder }] = await Promise.all([
        window.google.maps.importLibrary('maps'),
        window.google.maps.importLibrary('geocoding'),
      ]);
      return { Map, Circle, Geocoder };
    })();
    this.#mapsPromise.catch(() => (this.#mapsPromise = null));
    return this.#mapsPromise;
  }

  /** Open the map on a spot (a place, the GPS fix, a saved address being changed). */
  async #toMap(spot, title = 'Confirm delivery location') {
    this.draft = { ...spot };
    this.querySelector('[data-cw="map-title"]').textContent = title;
    this.#view('map');
    this.#paintSpot();
    this.#measure();
    // A picked place keeps its own name; reverse geocoding still fills what it lacks (an area
    // has no pincode of its own, and checkout needs one).
    if (!spot.named || !spot.pincode) this.#reverse(!!spot.named);
    try {
      const g = await this.#maps();
      const center = { lat: this.draft.lat, lng: this.draft.lng };
      if (!this.map) {
        const s = this.#settings;
        this.map = new g.Map(this.querySelector('[data-cw="canvas"]'), {
          center,
          zoom: 17,
          disableDefaultUI: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
          keyboardShortcuts: false,
        });
        if (s?.origin) {
          // The mill and the delivery edge (the design's rings were mock tiers; Flow has one).
          new g.Circle({ map: this.map, center: s.origin, radius: (s.max_km || 35) * 1000, strokeColor: '#9C4B22', strokeOpacity: 0.45, strokeWeight: 1.5, fillOpacity: 0, clickable: false });
          new g.Circle({ map: this.map, center: s.origin, radius: 45, strokeColor: '#FFFFFF', strokeWeight: 2, fillColor: '#6E4A2E', fillOpacity: 1, clickable: false });
        }
        this.geocoder = new g.Geocoder();
        this.map.addListener('dragstart', () => this.root.classList.add('is-moving'));
        this.map.addListener('idle', this.#onIdle);
      } else {
        this.map.setCenter(center);
        this.map.setZoom(17);
      }
    } catch {
      this.querySelector('[data-cw="canvas"]').innerHTML =
        '<p class="cw-addr__nomap">The map did not load. The spot is still fine: confirm it, or search again.</p>';
    }
  }

  /** The pin settled: if it moved, this is a new spot. */
  #onIdle = () => {
    this.root.classList.remove('is-moving');
    if (!this.map || !this.draft) return;
    const c = this.map.getCenter();
    if (Math.abs(c.lat() - this.draft.lat) < 1e-6 && Math.abs(c.lng() - this.draft.lng) < 1e-6) return;
    this.draft.lat = c.lat();
    this.draft.lng = c.lng();
    this.draft.named = false;
    this.#measure();
    this.#reverse();
  };

  /**
   * The name of the spot under the pin (Google's reverse geocoding). `fillOnly`: the spot is a
   * picked place, so only the parts it lacks (pincode, area, city) are filled in.
   */
  async #reverse(fillOnly = false) {
    const mine = ++this.#seq;
    try {
      const g = await this.#maps();
      if (!this.geocoder) this.geocoder = new g.Geocoder();
      const { results } = await this.geocoder.geocode({ location: { lat: this.draft.lat, lng: this.draft.lng } });
      if (mine !== this.#seq || !results?.length) return;
      const best = results[0];
      const parts = partsOf(best.address_components, 'long_name');
      if (fillOnly) {
        for (const key of ['pincode', 'area', 'city', 'street']) if (!this.draft[key] && parts[key]) this.draft[key] = parts[key];
        if (!this.draft.building) this.draft.building = buildingFrom(results);
      } else {
        const building = buildingFrom(results);
        Object.assign(this.draft, parts, { address: best.formatted_address || '', name: building || parts.area || parts.street || 'Your pin', building });
      }
    } catch {
      if (mine === this.#seq && !this.draft.name) this.draft.name = 'Your pin';
    }
    if (mine === this.#seq) this.#paintSpot();
  }

  /** Flow's answer for the spot: distance, and today / tomorrow / pick up only. */
  async #measure() {
    const spot = this.draft;
    spot.answer = null;
    this.#paintSvc({ tone: 'muted', text: 'Checking delivery…' });
    this.#measuring?.abort();
    const control = new AbortController();
    this.#measuring = control;
    try {
      const res = await quote(spot, control.signal);
      if (this.draft !== spot || control.signal.aborted) return;
      if (res.ok) {
        spot.answer = res.body;
        spot.km = Number(res.body.distance_km);
        spot.serviceable = !!res.body.serviceable;
      } else if (res.status === 429) {
        this.#paintSvc({ tone: 'muted', text: 'One moment…' });
        setTimeout(() => this.draft === spot && this.#measure(), 2500);
        return;
      } else {
        spot.km = null;
        spot.serviceable = null;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      spot.km = null;
      spot.serviceable = null;
    }
    this.#paintSpot();
  }

  #spotSvc(spot) {
    const a = spot.answer;
    if (!a) return spot.serviceable === null ? { tone: 'muted', text: 'We could not check this spot just now' } : { tone: 'muted', text: 'Checking delivery…' };
    if (!a.serviceable) return { tone: 'pickup', text: 'Pick up from our shop only' };
    const when = a.standard?.when || '';
    return { tone: /^tomorrow/i.test(when) ? 'tomorrow' : 'today', text: when ? `Delivers ${lowerFirst(when)}` : 'We deliver here' };
  }

  #paintSvc(svc) {
    const line = this.querySelector('[data-cw="spot-svc"]');
    line.dataset.tone = svc.tone;
    this.querySelector('[data-cw="spot-svc-text"]').textContent = svc.text;
  }

  #paintSpot() {
    const d = this.draft;
    if (!d) return;
    this.querySelector('[data-cw="spot-name"]').textContent = d.name || 'Finding the spot…';
    this.querySelector('[data-cw="spot-km"]').textContent = d.km != null && Number.isFinite(d.km) ? `· ${kmText(d.km)}` : '';
    this.querySelector('[data-cw="spot-addr"]').textContent =
      addressUnder(d.name, d.address) || (d.area && d.area !== d.name ? `${d.area}${d.city && d.city !== d.area ? `, ${d.city}` : ''}` : d.city || '');
    if (d.answer || d.serviceable === null) this.#paintSvc(this.#spotSvc(d));
  }

  /** The phone's own position. */
  #locate(fromMap) {
    if (!navigator.geolocation) {
      this.#note('This browser cannot find you. Search for your area instead.');
      return;
    }
    if (!fromMap) this.#note('Finding you…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.#note('');
        const spot = { lat: pos.coords.latitude, lng: pos.coords.longitude, address: '', name: '', named: false };
        if (fromMap && this.map) {
          this.map.panTo({ lat: spot.lat, lng: spot.lng });
        } else {
          this.fm = null;
          this.#toMap(spot);
        }
      },
      () => {
        const text = 'We could not get your location. Search for your area instead.';
        if (fromMap) this.#paintSvc({ tone: 'muted', text });
        else this.#note(text);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  /* ---------------------------------------------------------------- form */

  #blankForm() {
    const b = book();
    const mine = b.saved.find((a) => a.me !== false && a.name) || {};
    const body = document.body.dataset;
    return {
      editId: null,
      forWho: 'me',
      kind: b.saved.some((a) => kindOf(a) === 'home') ? 'work' : 'home',
      lbl: '',
      flat: '',
      floor: '',
      tower: '',
      apartment: '',
      landmark: '',
      name: mine.name || body.customerName || '',
      phone: mine.phone || body.customerPhone || '',
      rname: '',
      rphone: '',
      touched: {},
    };
  }

  #formFrom(a) {
    const me = a.me !== false;
    // An old-theme address has no name or phone: the signed-in shopper's own, as for a new one.
    const body = document.body.dataset;
    // A bare delivery location has no tag yet: it starts as a new address would (Home, or Work
    // when there is a Home), not as "Other" with a name to type.
    const bare = !a.tag && !a.flat && !a.apartment && !a.lines;
    const others = book().saved.filter((x) => x.id !== a.id);
    return {
      editId: a.id,
      forWho: me ? 'me' : 'other',
      kind: bare ? (others.some((x) => kindOf(x) === 'home') ? 'work' : 'home') : kindOf(a),
      lbl: kindOf(a) === 'other' ? a.tag || '' : '',
      flat: a.flat || '',
      floor: a.floor || '',
      tower: a.tower || '',
      apartment: a.apartment || a.building || '',
      landmark: a.landmark || '',
      name: me ? a.name || body.customerName || '' : '',
      phone: me ? a.phone || body.customerPhone || '' : '',
      rname: me ? '' : a.name || '',
      rphone: me ? '' : a.phone || '',
      touched: {},
    };
  }

  #toForm() {
    if (!this.fm) this.fm = this.#blankForm();
    const d = this.draft || {};
    // Only a real building (a searched society, or Google's building for the pin); an area
    // name here would be taken for the building and reach the rider (owner, 2 Oct).
    if (!this.fm.apartment && d.building) this.fm.apartment = d.building;
    this.querySelector('[data-cw="form-title"]').textContent = this.fm.editId ? 'Edit address details' : 'Add address details';
    this.#paintForm();
    this.#view('form');
  }

  #valid() {
    const f = this.fm;
    const me = f.forWho === 'me';
    const ok = {
      flat: !!f.flat.trim(),
      apartment: !!f.apartment.trim(),
      lbl: f.kind !== 'other' || !!f.lbl.trim(),
      // Myself: the name and number come from signing in (CHECKOUT asks for it), not typed here.
      rname: me || !!f.rname.trim(),
      rphone: me || phoneOk(f.rphone),
    };
    return { ok, all: Object.values(ok).every(Boolean) };
  }

  #paintForm() {
    const f = this.fm;
    const d = this.draft || {};
    const me = f.forWho === 'me';
    const { ok } = this.#valid();
    const bad = (key) => (f.touched[key] && ok[key] === false ? ' is-bad' : '');
    const field = (key, label, req, ph, type = 'text', extra = '') => `
      <label class="cw-addr__field">
        <span class="cw-addr__flabel">${esc(label)}${req ? '<span class="cw-addr__req" aria-hidden="true">*</span>' : ''}</span>
        <input class="cw-addr__input${bad(key)}" data-key="${key}" type="${type}" value="${esc(f[key])}" placeholder="${esc(ph || '')}"${req ? ' required aria-required="true"' : ''}${extra}>
      </label>`;
    const chip = (on, act, value, inner) =>
      `<button type="button" class="cw-addr__chip${on ? ' is-on' : ''}" data-act="${act}" data-value="${value}" aria-pressed="${on}">${inner}</button>`;
    const telAttrs = ' inputmode="tel" autocomplete="tel-national" maxlength="20"';
    this.querySelector('[data-cw="form"]').innerHTML = `
      ${f.ask ? '<p class="cw-addr__ask">Add your flat and building so the rider finds you.</p>' : ''}
      <div class="cw-addr__loc">
        <span class="cw-addr__loc-icon">${this.icons.mappin}</span>
        <span class="cw-addr__loc-text">
          <span class="cw-addr__loc-name">${esc(f.apartment || d.name || 'Your pin')}</span>
          <span class="cw-addr__loc-addr">${esc(addressUnder(f.apartment || d.name, d.address) || d.area || '')}</span>
        </span>
        <button type="button" class="cw-addr__change" data-act="form-change">CHANGE</button>
      </div>
      <div class="cw-addr__group" role="group" aria-labelledby="cw-addr-who">
        <p class="cw-addr__glabel" id="cw-addr-who">Who is it for?</p>
        <div class="cw-addr__chips">
          ${chip(me, 'who', 'me', '<span class="cw-addr__dot"></span>Myself')}
          ${chip(!me, 'who', 'other', '<span class="cw-addr__dot"></span>Someone else')}
        </div>
      </div>
      <div class="cw-addr__group" role="group" aria-labelledby="cw-addr-kind">
        <p class="cw-addr__glabel" id="cw-addr-kind">Save as<span class="cw-addr__req" aria-hidden="true">*</span></p>
        <div class="cw-addr__chips cw-addr__chips--wrap">
          ${KINDS.map(([id, n]) => chip(f.kind === id, 'kind', id, `<span class="cw-addr__chipicon">${this.icons[id]}</span>${n}`)).join('')}
        </div>
      </div>
      <div class="cw-addr__fields">
        ${f.kind === 'other' ? field('lbl', 'Name this address', true, 'e.g. Priya’s place') : ''}
        ${field('flat', 'Flat / house no.', true, 'e.g. Flat 1163', 'text', ' autocomplete="address-line2"')}
        ${field('apartment', 'Building / society name', true, 'e.g. Prestige Lakeside Habitat')}
        <div class="cw-addr__pair">
          ${field('floor', 'Floor', false, 'e.g. 16')}
          ${field('tower', 'Tower / block', false, 'e.g. Tower 1')}
        </div>
        ${field('landmark', 'Nearby landmark', false, 'e.g. Opposite Infosys Gate 1')}
      </div>
      ${
        me
          ? ''
          : `<div class="cw-addr__fields">
        <p class="cw-addr__eyebrow cw-addr__eyebrow--form">RECEIVER’S DETAILS</p>
        ${field('rname', 'Receiver’s name', true, '')}
        ${field('rphone', 'Receiver’s phone', true, '10-digit mobile', 'tel', telAttrs)}
      </div>`
      }`;
    this.#paintSave();
  }

  #paintSave() {
    this.querySelector('[data-act="save"]').disabled = !this.#valid().all;
  }

  #onField = (event) => {
    const key = event.target.dataset?.key;
    if (!key || !this.fm) return;
    this.fm[key] = event.target.value;
    if (key === 'apartment') {
      const name = this.querySelector('.cw-addr__loc-name');
      if (name) name.textContent = event.target.value || this.draft?.name || 'Your pin';
    }
    if (event.target.classList.contains('is-bad')) {
      const { ok } = this.#valid();
      event.target.classList.toggle('is-bad', ok[key] === false);
    }
    this.#paintSave();
  };

  #onBlur = (event) => {
    const key = event.target.dataset?.key;
    if (!key || !this.fm) return;
    this.fm.touched[key] = true;
    // A pasted "+91 98450 12345" becomes "98450 12345": the +91 is already understood.
    if ((key === 'phone' || key === 'rphone') && phoneOk(event.target.value)) {
      event.target.value = phoneText(event.target.value);
      this.fm[key] = event.target.value;
    }
    const { ok } = this.#valid();
    event.target.classList.toggle('is-bad', ok[key] === false);
  };

  /**
   * "Set location": the spot alone becomes the delivery location, and the sheet closes. One such
   * bare spot at a time: a new one replaces the last that never got its details. It is complete
   * (flat and building) at CHECKOUT, through the same form (cw-delivery.js).
   */
  #saveSpot() {
    const d = this.draft || {};
    if (d.lat == null || d.lng == null) return;
    const b = book();
    const bare = (a) => !a.flat && !a.apartment && !a.shopifyId && !a.lines;
    const chosen = b.saved.find((a) => a.id === b.chosenId);
    const reuse = chosen && bare(chosen) ? chosen : b.saved.find(bare);
    const record = {
      id: reuse?.id || `a${Date.now().toString(36)}`,
      kind: 'other',
      tag: '',
      label: d.building || d.name || d.area || '',
      building: d.building || '',
      me: true,
      lat: d.lat,
      lng: d.lng,
      address: d.address || '',
      shortAddress: shortAddress(d.address),
      area: d.area || '',
      street: d.street || '',
      city: d.city || '',
      pincode: d.pincode || '',
      km: d.km != null && Number.isFinite(d.km) ? d.km : null,
    };
    const at = b.saved.findIndex((a) => a.id === record.id);
    if (at >= 0) b.saved[at] = record;
    else b.saved.unshift(record);
    b.chosenId = record.id;
    saveBook(b);
    this.fm = null;
    this.draft = null;
    this.close();
  }

  #save() {
    const f = this.fm;
    const check = this.#valid();
    if (!check.all) {
      for (const key of Object.keys(check.ok)) f.touched[key] = true;
      this.#paintForm();
      this.querySelector('.cw-addr__input.is-bad')?.focus();
      return;
    }
    const d = this.draft || {};
    const me = f.forWho === 'me';
    const b = book();
    const old = b.saved.find((a) => a.id === f.editId) || {};
    const record = {
      ...old,
      id: f.editId || `a${Date.now().toString(36)}`,
      kind: f.kind,
      tag: f.kind === 'other' ? f.lbl.trim() : KINDS.find(([id]) => id === f.kind)[1],
      label: f.apartment.trim(),
      apartment: f.apartment.trim(),
      flat: f.flat.trim(),
      floor: f.floor.trim(),
      tower: f.tower.trim(),
      landmark: f.landmark.trim(),
      me,
      // Myself: what was on the address before (an account address has it), else nothing;
      // checkout and the account take the signed-in shopper's own (cw-delivery.js, cw-book-sync.js).
      name: (me ? old.me !== false ? old.name || '' : '' : f.rname).trim(),
      phone: me ? (old.me !== false ? old.phone || '' : '') : tenDigits(f.rphone),
      lat: d.lat ?? old.lat,
      lng: d.lng ?? old.lng,
      address: d.address ?? old.address ?? '',
      shortAddress: shortAddress(d.address ?? old.address),
      area: d.area ?? old.area ?? '',
      // Checkout's line 1 names the street (cw-delivery.js checkoutLines).
      street: d.street ?? old.street ?? '',
      city: d.city ?? old.city ?? '',
      pincode: d.pincode ?? old.pincode ?? '',
      km: d.km != null && Number.isFinite(d.km) ? d.km : old.km ?? null,
    };
    // Edited here, so its own fields make checkout's lines from now on (cw-delivery.js).
    delete record.lines;
    const at = b.saved.findIndex((a) => a.id === record.id);
    if (at >= 0) b.saved[at] = record;
    else b.saved.unshift(record);
    b.chosenId = record.id;
    saveBook(b);
    // Onto the shopper's account when signed in (cw-book-sync.js); a miss is retried on sync.
    pushAddress(record.id);
    this.fm = null;
    this.draft = null;
    this.close();
  }

  /* ---------------------------------------------------------------- menu */

  #openMenu(id, asking = false) {
    const b = book();
    const a = b.saved.find((x) => x.id === id);
    if (!a) return;
    this.menuId = id;
    const label = a.tag || 'Address';
    const where = [a.apartment || a.label, addressUnder(a.apartment || a.label, a.address) || a.area].filter(Boolean).join(', ');
    const sheet = this.querySelector('[data-cw="menu-sheet"]');
    const chosenOne = a.id === b.chosenId;
    sheet.innerHTML = asking
      ? `
        <div class="cw-addr__handle" aria-hidden="true"></div>
        <div class="cw-addr__ask">
          <div class="cw-addr__asktext">
            <p class="cw-addr__asktitle" id="cw-addr-menu-title">Delete ${esc(label)}?</p>
            <p class="cw-addr__askaddr">${esc(where)}</p>
          </div>
          <div class="cw-addr__askbtns">
            <button type="button" class="cw-addr__askbtn" data-act="menu-close">CANCEL</button>
            <button type="button" class="cw-addr__askbtn cw-addr__askbtn--go" data-act="delete" data-id="${esc(a.id)}">DELETE</button>
          </div>
        </div>`
      : `
        <div class="cw-addr__handle" aria-hidden="true"></div>
        <div class="cw-addr__menuhead">
          <div class="cw-addr__menutext">
            <p class="cw-addr__menutitle" id="cw-addr-menu-title">${esc(label)}</p>
            <p class="cw-addr__menuaddr">${esc(where)}</p>
          </div>
          <button type="button" class="cw-addr__x" data-act="menu-close" aria-label="Close">${this.icons.x}</button>
        </div>
        <div class="cw-addr__menulist">
          <button type="button" class="cw-addr__menuitem" data-act="edit" data-id="${esc(a.id)}">${this.icons.edit}<span>Edit address</span></button>
          <div class="cw-addr__menuline"></div>
          <button type="button" class="cw-addr__menuitem" data-act="menu-pin" data-id="${esc(a.id)}">${this.icons.pin}<span>${a.id === b.pinnedId ? 'Unpin' : 'Pin to top'}</span></button>
          <div class="cw-addr__menuline"></div>
          ${
            chosenOne
              ? `<div class="cw-addr__menuitem is-off">${this.icons.trash}<span class="cw-addr__menunote"><span>Delete address</span><span class="cw-addr__menusub">This is your delivery address. Pick another one first.</span></span></div>`
              : `<button type="button" class="cw-addr__menuitem cw-addr__menuitem--del" data-act="ask-delete" data-id="${esc(a.id)}">${this.icons.trash}<span>Delete address</span></button>`
          }
        </div>`;
    this.querySelector('[data-cw="menu"]').hidden = false;
    requestAnimationFrame(() => sheet.querySelector('button')?.focus());
  }

  #closeMenu() {
    const menu = this.querySelector('[data-cw="menu"]');
    if (!menu || menu.hidden) return;
    menu.hidden = true;
    const back = this.querySelector(`[data-act="more"][data-id="${CSS.escape(this.menuId || '')}"]`);
    back?.focus({ preventScroll: true });
  }

  #delete(id) {
    const b = book();
    if (id === b.chosenId) return;
    // Off the shopper's account too (cw-book-sync.js), so it does not come back on sync.
    dropAddress(b.saved.find((a) => a.id === id));
    b.saved = b.saved.filter((a) => a.id !== id);
    if (b.pinnedId === id) b.pinnedId = null;
    saveBook(b);
    this.querySelector('[data-cw="menu"]').hidden = true;
    this.#paintList();
  }

  /* ---------------------------------------------------------------- taps */

  #onAct = (event) => {
    const hit = /** @type {HTMLElement} */ (event.target).closest('[data-act]');
    if (!hit || !this.contains(hit)) return;
    const act = hit.dataset.act;
    const id = hit.dataset.id;
    switch (act) {
      case 'pick':
        return this.#pick(id);
      case 'pin':
        return this.#pin(id);
      case 'more':
        return this.#openMenu(id);
      case 'menu-close':
        return this.#closeMenu();
      case 'menu-pin':
        this.querySelector('[data-cw="menu"]').hidden = true;
        return this.#pin(id);
      case 'ask-delete':
        return this.#openMenu(id, true);
      case 'delete':
        return this.#delete(id);
      case 'edit':
        return this.#editFor(id);
      case 'clear':
      case 'mclear':
        this.#clearQuery();
        (act === 'clear' ? this.querySelector('[data-cw="q"]') : this.querySelector('[data-cw="mq"]')).focus();
        return;
      case 'locate':
        return this.#locate(this.root.dataset.view === 'map');
      case 'add': {
        this.mode = 'address';
        this.fm = null;
        const here = book().saved.find((a) => a.id === book().chosenId);
        const s = this.#settings;
        const start = here?.lat != null ? { lat: here.lat, lng: here.lng } : s?.origin || { lat: 12.8192, lng: 77.6638 };
        return this.#toMap({ ...start, address: '', name: '', named: false });
      }
      case 'place': {
        const fromMap = !!hit.closest('[data-cw="msearch"]');
        hit.disabled = true;
        this.#place(id, hit.dataset.main)
          .then((spot) => {
            // A place from the list starts a new address; from the map it moves this one.
            if (fromMap) this.querySelector('[data-cw="msearch"]').hidden = true;
            else this.fm = null;
            return this.#toMap(spot);
          })
          .catch(() => {
            hit.disabled = false;
            this.#note('We could not open that place. Try another.');
          });
        return;
      }
      case 'back':
        if (this.fm?.editId) {
          // Changing a saved address's spot: back to its details.
          return this.#toForm();
        }
        this.#view('list');
        this.#paintList();
        return;
      case 'map-search': {
        const box = this.querySelector('[data-cw="msearch"]');
        box.hidden = false;
        requestAnimationFrame(() => this.querySelector('[data-cw="mq"]').focus());
        return;
      }
      case 'msearch-back':
        this.querySelector('[data-cw="msearch"]').hidden = true;
        return;
      case 'confirm':
        if (!this.fm?.editId && this.mode !== 'address') return this.#saveSpot();
        return this.#toForm();
      case 'form-change':
        return this.#toMap({ ...this.draft, named: true }, this.fm?.editId ? 'Edit address' : 'Confirm delivery location');
      case 'form-close':
        if (this.fm?.editId) {
          this.fm = null;
          this.#view('list');
          this.#paintList();
          return;
        }
        this.#view('map');
        return;
      case 'who':
      case 'kind':
        if (!this.fm) return;
        this.fm[act === 'who' ? 'forWho' : 'kind'] = hit.dataset.value;
        this.#paintForm();
        this.querySelector(`[data-act="${act}"][data-value="${hit.dataset.value}"]`)?.focus();
        return;
      case 'save':
        return this.#save();
      default:
    }
  };
}

if (!customElements.get('cw-address')) customElements.define('cw-address', CwAddress);

// The book follows the signed-in shopper's account, once a visit (cw-book-sync.js).
syncBook();
